import { type TNormalizedList } from "./types";

/**
 * Adapts any of the backend's inconsistent list envelopes into a uniform
 * `{ rows, total }`. Handles:
 *  - `{ items, total_count, limit, offset }`               (most admin endpoints)
 *  - `{ data: [...], meta: { pagination: { total, ... } } }` (users)
 *  - `{ results | events, total_count }`                   (audit)
 *  - `{ threads | categories, total, page, size }`         (chat)
 *  - bare `T[]`                                            (client mode)
 *
 * Service methods in this app often wrap the payload in `{ data: <envelope> }`;
 * we unwrap one level of that when the inner object also looks like an envelope.
 */
export function normalizeListResponse<TRow>(raw: unknown): TNormalizedList<TRow> {
  if (raw == null) {
    return { rows: [], total: 0 };
  }

  // Bare array → client mode.
  if (Array.isArray(raw)) {
    return { rows: raw as TRow[], total: raw.length };
  }

  if (typeof raw !== "object") {
    return { rows: [], total: 0 };
  }

  const obj = raw as Record<string, unknown>;

  // Service wrapper: `{ data: <envelope|array> }`. Unwrap when `data` is an
  // array, or an object that itself carries a list key (avoids treating the
  // users' `{ data: [...] }` list envelope as a wrapper by mistake — that case
  // is handled explicitly below).
  if ("data" in obj && !Array.isArray(obj.data) && obj.data && typeof obj.data === "object") {
    const inner = obj.data as Record<string, unknown>;
    if (hasListKey(inner)) {
      return normalizeEnvelope<TRow>(inner);
    }
  }

  return normalizeEnvelope<TRow>(obj);
}

const LIST_KEYS = ["items", "results", "events", "threads", "categories", "rows"] as const;

function hasListKey(obj: Record<string, unknown>): boolean {
  return LIST_KEYS.some((key) => Array.isArray(obj[key]));
}

function normalizeEnvelope<TRow>(obj: Record<string, unknown>): TNormalizedList<TRow> {
  // users: `{ data: [...], meta: { pagination: { total } } }`
  if (Array.isArray(obj.data)) {
    const meta = obj.meta as { pagination?: { total?: number } } | undefined;
    const total = meta?.pagination?.total;
    return {
      rows: obj.data as TRow[],
      total: typeof total === "number" ? total : obj.data.length,
    };
  }

  // Find the first known list key present.
  for (const key of LIST_KEYS) {
    if (Array.isArray(obj[key])) {
      const rows = obj[key] as TRow[];
      const total = readTotal(obj);
      return { rows, total: total ?? rows.length };
    }
  }

  return { rows: [], total: 0 };
}

function readTotal(obj: Record<string, unknown>): number | undefined {
  const candidates = ["total_count", "total", "count"];
  for (const key of candidates) {
    const value = obj[key];
    if (typeof value === "number") return value;
  }
  return undefined;
}
